import axios from "axios";
import config from "config.json";
import log from "loglevel";
import {allInterceptors, InterceptorName, Interceptors,} from "@/lib/api_interceptors";
import {toast} from "react-hot-toast";
import {IPaginatedResponse} from "@/types/IGenericResponse.ts";

export interface RequestProps {
}

export interface PaginatedRequestProps extends RequestProps {
  page?: number;
  limit?: number;
  query?: string;
  params?: Record<string, string>;
  fetchAll?: boolean;
}

interface GetApiClientProps {
  contentType?: string;
  interceptors?: InterceptorName[];
}

export const getDefaultApiClient = () => {
  return getApiClient({
    contentType: "application/json",
  });
};

export const getApiClient = ({
                               contentType,
                               interceptors,
                             }: GetApiClientProps) => {
  contentType = contentType || "application/json";
  interceptors = interceptors || allInterceptors();

  let client = axios.create({
    baseURL: config.backend.baseURL,
    headers: {
      "Content-Type": contentType,
      Accept: "application/json",
      "X-Client-Version": config.versions.frontend,
      "X-Api-Version": config.versions.backend,
    },
    timeout: config.backend.timeout,
    withCredentials: true,
    withXSRFToken: true,
    xsrfCookieName: "_csrf",
    xsrfHeaderName: "X-XSRF-TOKEN",
    maxRedirects: 0, // Add this line to prevent automatic redirects
  });

  client.interceptors.request.use(
    (request) => {
      // log the request payload
      if (config.logging.logRequests) {
        log.trace("Request", request.url, request);
      }
      return request;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  client.interceptors.response.use(
    (response) => {
      // log the response payload
      if (config.logging.logResponses) {
        log.trace("Response", response.config.url, response);
        if (response.headers["x-readonly"] === "true") {
          log.debug("Readonly mode enabled");
        }
      }

      // As XMLHttpRequest is responsible for handling redirects, axios will not follow the redirect
      // which means in some scenarios, the client will not be redirected to the new location.
      // this happens when the browser considers the redirect unsafe
      // for this reason, if the server, returns a 200 but with a redirect header, we need to handle it
      // ourselves
      const location = response.headers?.["x-location"];

      if (response.status === 200 && location) {
        window.location.href = location;
      }

      return response;
    },
    async (error) => {
      if (error.code === "ERR_NETWORK") {
        toast.error(
          "Could not connect to the server"
        );
        return Promise.reject(error);
      }

      try {
        for (const interceptor of interceptors) {
          await Interceptors[interceptor](error);
        }
        return Promise.reject(error);
      } catch (interceptorError: any) {
        // Handle redirect rejections
        if (interceptorError?.redirect) {
          window.location.href = interceptorError.redirect;
        }
        return Promise.reject(interceptorError);
      }
    },
  );

  return client;
};

export const apiClient = getApiClient({contentType: "application/json"});

export async function getRequest<T>(
  url: string,
): Promise<T> {
  return apiClient.get(`${url}`).then((response) => {
    return response.data as T;
  });
}

export async function uploadRequest<T>(
  url: string,
  payload: any,
): Promise<T> {
  return getApiClient({contentType: "multipart/form-data"})
    .post(`${url}`, payload)
    .then((response) => {
      return response.data as T;
    });
}

export async function getPaginatedRequest<T>(
  url: string,
  {fetchAll, ...props}: PaginatedRequestProps,
): Promise<IPaginatedResponse<T>> {
  if (fetchAll) {
    let result: IPaginatedResponse<T>;
    return getPaginatedRequestSingle<T>(url, props).then((response) => {
      result = response;
      if (response.next !== 0) {
        return getPaginatedRequest<T>(url, {
          ...props,
          page: response.next,
          fetchAll: true,
        }).then((nextResponse) => {
          result.data = result.data.concat(nextResponse.data);
          return result;
        });
      }
      return result;
    });
  } else {
    return getPaginatedRequestSingle<T>(url, props);
  }
}

export async function getPaginatedRequestSingle<T>(
  url: string,
  {params, page, limit, query}: PaginatedRequestProps,
): Promise<IPaginatedResponse<T>> {
  const searchParams = new URLSearchParams();

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      searchParams.append(key, value);
    }
  }

  if (query) {
    searchParams.set("q", query);
  }
  if (page !== undefined) {
    searchParams.set("page", page.toString());
  }
  if (limit !== undefined) {
    searchParams.set("limit", limit.toString());
  }

  const finalUrl = `${url}?${searchParams.toString()}`;

  log.debug("Fetching paginated request", finalUrl);
  return apiClient.get(finalUrl).then((response) => {
    return response.data as IPaginatedResponse<T>;
  });
}

